import {
    Timestamp,
    arrayUnion,
    increment,
    orderBy,
    where,
} from 'firebase/firestore'
import { traceAsync, traceEvent } from '@/lib/diagnostics/offerModalTrace'
import type { CancellationQuote } from '@/lib/cancellation/cancellationQuote'
import {
    AdditionaLBookingResult,
    AddressDetails,
    BookOrder,
    CleanseData,
    CommercialAccount,
    DisconnectObject,
    DismantleObject,
    FailReasons,
    Fee,
    GetAddressIOAutocompleteResponse,
    GetAddressIOResponse,
    GroupResponse,
    Holiday,
    Item,
    JobStatus,
    Mode,
    PartnerAPIResponse,
    PaymentMethods,
    PayoutCategory,
    PricingResponse,
    PricingSchedule,
    PromoCode,
    SalesOwner,
    SearchResult,
    ServicePricing,
    SlackMessageDetails,
    Surge,
    Team,
    Timeline,
    UsageBuckets,
} from '../types'
import { isExemptFromTippingTicket } from '@LITTA-CO/shared'
import { mapToPricingFees } from '@/lib/api/pricing'
import {
    isJobTerminalStatus,
    resolveDisplayTaskStatusWithLookup,
    type JobStatusLookup,
} from '@/lib/fulfilment/taskStatusMapping'
import {
    addCleanseData,
    addKeyEvent,
    getJobStatuses,
    getLoadAndAdditionalItems,
    getPayoutCategories,
    getActiveServicePricingItemsFromDb,
    updateFirebaseDocument,
    isStaging as is,
    isQA,
} from './firebase'
import axios from 'axios'
import { cloneDeep, get, isEqual } from 'lodash'

const apiKey = 'mQ1gZYmz1U-acU_TBHdpiQ9608'
const urlParams = new URLSearchParams(window.location.search)
const isStaging = urlParams.get('staging') === 'true' || (is ? is : false)

// const baseUrl = isStaging
//     ? 'https://us-central1-staging-9c37f.cloudfunctions.net'
//     : 'https://us-central1-book-litta-co.cloudfunctions.net'

const baseUrlGen2 = isStaging
    ? 'https://europe-west2-staging-9c37f.cloudfunctions.net'
    : 'https://europe-west2-book-litta-co.cloudfunctions.net'

export const dateOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
}

export const capitaliseFirstLetter = (string: string | null | undefined) => {
    if (!string) return string
    return string.charAt(0).toUpperCase() + string.slice(1)
}

export const EMPTY_STRING = ''

export const UserTypeColour = {
    new: '#7C37EF',
    returning_converted: '#048A0E',
    returning_unconverted: '#FFBA05',
} as const

export enum QueryType {
    Reference = 0,
    Email,
    PhoneNumber,
    Postcode,
    FirebaseId,
}

export enum Operator {
    Includes = '<>',
    Equal = '=',
    NotEqual = '!=',
    LessThan = '<',
    LessThanOrEqual = '<=',
    MoreThan = '>',
    MoreThanOrEqual = '>=',
}

const checkIfEmail = (text: string) => {
    const re =
        /(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))/
    return re.test(text)
}

const checkIfPhoneNumber = (text: string) => {
    const re =
        /^\s*(?:\+?(\d{1,3}))?[-. (]*(\d{3})[-. )]*(\d{3})[-. ]*(\d{4})(?: *x(\d+))?\s*$/g
    return re.test(text)
}

const checkIfPostcode = (text: string) => {
    const re =
        /([Gg][Ii][Rr] 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9][A-Za-z]?))))\s?[0-9][A-Za-z]{2})/g
    return text.length < 10 && re.test(text)
}

export const getQueryType = (value: string): QueryType => {
    const isEmail = checkIfEmail(value)
    const isNumber = checkIfPhoneNumber(value)
    const isPostcode = checkIfPostcode(value)
    const isReference = value?.startsWith('LT') && value?.length < 14

    if (isEmail) return QueryType.Email
    if (isNumber) return QueryType.PhoneNumber
    if (isPostcode) return QueryType.Postcode
    if (isReference) return QueryType.Reference
    return QueryType.FirebaseId
}

export const getWhereQuery = (queryType: QueryType, value: string) => {
    const orderByFunction = orderBy('created_at', 'desc')

    switch (queryType) {
        case QueryType.Reference:
            return [where('LTA', '==', value)]
        case QueryType.Email:
            return [where('userDetails.email', '==', value), orderByFunction]
        case QueryType.PhoneNumber:
            return [
                where('userDetails.mobilenumber', '==', value),
                orderByFunction,
            ]
        case QueryType.Postcode:
            return [
                where('address_details.postcode', '==', value),
                orderByFunction,
            ]
        case QueryType.FirebaseId:
            return [where('recordId', '==', value)]
    }
}

export const secondsToDate = (seconds?: number) => {
    if (!seconds) return null

    return new Date(seconds * 1000)
}

export const getLatestStatus = (
    timeline: Timeline[] | null | undefined,
    getAllData?: boolean,
): string | Timeline | null => {
    if (!timeline) return null

    const latestStatus = timeline.reduce((prev, curr) => {
        if (prev > curr) return prev
        return curr
    })

    if (!getAllData) return latestStatus.status
    return latestStatus
}

export const addClass = (
    value: string | null,
    className: string,
    statuses?: JobStatus[],
) => {
    if (statuses) {
        const status = statuses.find(status => status.status == value)
        if (!status) return `<span class='${className}'>${value}</span>`
        return `<span class='${className}' style='background-color: ${status.markerColourHex};'>${value}</span>`
    }
    return `<span class='${className}'>${value}</span>`
}

export const getStatusFromCacheOrDB = async (localStorage: Storage) => {
    const cachedStatuses = localStorage.getItem('jobStatuses')

    if (cachedStatuses) {
        return JSON.parse(cachedStatuses) as JobStatus[]
    }

    const statuses = await getJobStatuses()
    if (statuses) {
        localStorage.setItem('jobStatuses', JSON.stringify(statuses))
    }
    return statuses
}

const isLetter = (str: any) => {
    if (typeof str === 'string' || str instanceof String) {
        return str?.substring(0, 1).match(/[a-zA-Z]/)
    }
    return false
}

const uppercase = (value: string) => {
    if (isLetter(value)) {
        return `${value.substring(0, 1).toUpperCase()}${value.substring(
            1,
            value.length,
        )}`
    }
    return value
}

const formatTimestamp = (
    timestamp: Timestamp | null | undefined,
): string | undefined => {
    if (!timestamp) return undefined

    try {
        let date: Date
        if (timestamp.toDate && typeof timestamp.toDate === 'function') {
            date = timestamp.toDate()
        } else if (timestamp.seconds) {
            date = new Date(timestamp.seconds * 1000)
        } else {
            return undefined
        }

        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        })
    } catch {
        return undefined
    }
}

const formatCurrency = (
    value: number | null | undefined,
): string | undefined => {
    if (value === null || value === undefined) return undefined

    try {
        return new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: 'GBP',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(value)
    } catch {
        return undefined
    }
}

export const convertResultsToSearch = (
    results: BookOrder[],
    jobStatuses: JobStatus[],
    doNotSort: boolean,
    jobStatusLookup: JobStatusLookup | null = null,
): SearchResult[] => {
    return results
        .filter(result => result.recordId)
        .sort((a, b) =>
            doNotSort
                ? 0
                : b?.selectedDateAndTime?.seconds -
                  a?.selectedDateAndTime?.seconds,
        )
        .map(result => {
            const reference =
                result.LTA ?? result.quote_ref ?? 'Unknown reference'
            const latestStatus = resolveDisplayTaskStatusWithLookup(
                result,
                jobStatusLookup,
            )
            const status =
                latestStatus &&
                latestStatus !== 'Unknown' &&
                latestStatus !== 'Loading...'
                    ? latestStatus
                    : result.LTA
                      ? 'Not assigned'
                      : 'Unpaid quote'
            const userIdentifier =
                result.userDetails?.email ??
                result.userDetails?.mobilenumber ??
                result.userDetails?.name ??
                'N/A'
            const items = result?.items
                ?.map(item => item['LITTA Service'])
                .join(', ')
            const firebaseId = result.recordId
            const userId = result.userId
            const enquiryType =
                result?.enquiry_type ?? result?.client_type ?? 'Unknown'

            // Parse collectionDate from selectedDateAndTime
            const collectionDate = formatTimestamp(result.selectedDateAndTime)

            // Parse bookedDate from paid_at
            const bookedDate = formatTimestamp(result.paid_at)

            // Get postcode from address_details
            const postcode = result.address_details?.postcode

            // Get total in priority order: total > final_invoice_value > draft_invoice_value
            const total = formatCurrency(
                result.total ??
                    result.final_invoice_value ??
                    result.draft_invoice_value,
            )

            return {
                firebaseId,
                reference,
                status: addClass(status, 'coloured', jobStatuses),
                user: userIdentifier,
                userId,
                items,
                // date: addClass(date ?? null, "date"),
                enquiryType: uppercase(enquiryType),
                collectionDate,
                bookedDate,
                postcode,
                total,
            }
        })
}

export const camelCaseToWords = (camelCase: string) => {
    const words = camelCase
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, function (str) {
            return str.toUpperCase()
        })
    return words
}

const CANT_UPLOAD_REASON_LABELS: Record<string, string> = {
    waste_not_ready_yet: "My waste isn't ready yet",
    no_access_to_waste: "I don't have access to the waste yet",
    booking_on_behalf:
        "I'm booking on behalf of someone else who has the waste",
    no_suitable_device: "I don't have a suitable device or camera",
    technical_issue: "I'm having a technical issue",
    confidential_or_sensitive:
        "It's confidential or sensitive waste I'd rather not photograph",
    urgent_or_same_day:
        "This is an urgent or same-day booking and I don't have time",
    other: 'Other',
}

/** Display reason when a customer said they can't upload images (fields and/or key event). */
export const getCantUploadImageReason = (
    order?: {
        cant_upload_image?: boolean
        cant_upload_reason?: string
        cant_upload_extra_info?: string
        keyEventsTimeline?: { type?: string; source?: string }[]
    } | null,
): string | null => {
    if (!order) return null

    if (order.cant_upload_image) {
        const reasonKey = order.cant_upload_reason ?? ''
        const reasonLabel = CANT_UPLOAD_REASON_LABELS[reasonKey] ?? reasonKey
        const extra = order.cant_upload_extra_info?.trim()

        if (reasonLabel && extra) return `${reasonLabel} - ${extra}`
        if (reasonLabel) return reasonLabel
        if (extra) return extra
        return 'Yes'
    }

    const events = order.keyEventsTimeline
    if (!Array.isArray(events) || !events.length) return null

    const match = [...events].reverse().find(event => {
        const type = (event.type ?? '')
            .toLowerCase()
            .replace(/[\u2018\u2019]/g, "'")

        return type.includes("can't upload")
    })

    if (!match?.type) return null

    const separatorIndex = match.type.indexOf(':')
    if (separatorIndex === -1) return match.type

    return match.type.slice(separatorIndex + 1).trim() || match.type
}

export const secondsToHms = (d: number, removeSeconds?: boolean) => {
    const h = Math.floor(d / 3600)
    const m = Math.floor((d % 3600) / 60)
    const s = Math.floor((d % 3600) % 60)

    const hDisplay = h > 0 ? h + (h == 1 ? ' hour, ' : ' hours, ') : ''
    const mDisplay = m > 0 ? m + (m == 1 ? ' minute, ' : ' minutes, ') : ''
    const sDisplay = s > 0 ? s + (s == 1 ? ' second' : ' seconds') : ''

    if (removeSeconds) {
        const text = hDisplay + mDisplay
        return text.substring(0, text.lastIndexOf(','))
    }

    return hDisplay + mDisplay + sDisplay
}

const padNumber = (num: number) => {
    return num.toString().padStart(2, '0')
}

export const getTodayStringDate = (date?: Date, padDay?: boolean) => {
    const now = date ?? new Date()
    const day = padDay ? padNumber(now.getDate()) : now.getDate()
    const month = padNumber(now.getMonth() + 1)
    const year = now.getFullYear()

    return `${day}-${month}-${year}`
}

export const toPaddedDdMmYyyy = (dateStr?: string | null) => {
    if (!dateStr) return dateStr ?? null
    const parts = dateStr.split('-').map(part => part.trim())
    if (parts.length !== 3) return dateStr
    const [day, month, year] = parts
    if (!day || !month || !year) return dateStr
    return `${day.padStart(2, '0')}-${month.padStart(2, '0')}-${year}`
}

export const getRuleOperator = (rule: string) => {
    if (rule.includes('<>')) return Operator.Includes
    if (rule.includes('>=')) return Operator.MoreThanOrEqual
    if (rule.includes('!=')) return Operator.NotEqual
    if (rule.includes('<')) return Operator.LessThan
    if (rule.includes('=')) return Operator.Equal

    // INCOMPLETE

    return Operator.Equal
}

export const autoConvert = (value: string) => {
    const converted = parseFloat(value)
    return Number.isNaN(converted) ? value : +converted
}

export const compareValue = (
    a: number | string,
    b: number | string,
    op: Operator,
) => {
    if (
        op === Operator.Includes &&
        typeof a === 'string' &&
        typeof b === 'string'
    ) {
        return (a as string).includes(b as string)
    }

    if (op === Operator.Equal) {
        return a === b
    }

    if (op === Operator.MoreThanOrEqual) {
        return a >= b
    }

    if (op === Operator.NotEqual) {
        return a !== b
    }

    if (op === Operator.LessThan) {
        return a < b
    }

    // INCOMPLETE

    return a === b
}

export const checkTimeSlotWindow = (timeSlot: string) => {
    try {
        if (!timeSlot || timeSlot == '') return 999

        const timeSlotEndHours = timeSlot.split('-')[1].split(':')[0].trim()
        const timeSlotEndMins = timeSlot.split('-')[1].split(':')[1].trim()
        const timeSlotEndDate = new Date()

        timeSlotEndDate.setHours(+timeSlotEndHours, +timeSlotEndMins, 0, 0)

        const now = new Date()
        const timeRemaining = +(
            (timeSlotEndDate.getTime() - now.getTime()) /
            1000 /
            60
        ).toFixed(0)

        return timeRemaining
    } catch (err) {
        console.log('Error checking timeslot window')
        return 999
    }
}

const units = {
    year: 24 * 60 * 60 * 1000 * 365,
    month: (24 * 60 * 60 * 1000 * 365) / 12,
    day: 24 * 60 * 60 * 1000,
} as Record<string, number>

export const getRelativeTime = (d1: Date, d2 = new Date()) => {
    try {
        const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' })
        const elapsed = d1.getTime() - d2.getTime()

        // "Math.abs" accounts for both "past" & "future" scenarios
        for (const u in units)
            if (Math.abs(elapsed) > units[u] || u == 'day') {
                return rtf?.format(
                    Math.round(elapsed / units[u]),
                    u as Intl.RelativeTimeFormatUnit,
                )
            }
    } catch (err) {
        return 'N/A'
    }
}

const getAccessToken = async () => {
    const refreshToken =
        '1//03KddvpJaEUrdCgYIARAAGAMSNwF-L9IruIdaya7JkIH83_POJcc2MAjO3SfaoCy5jPOQfiOTX5Y_9Vl8yQa6kqnIapE_y44n5io'

    const data = JSON.stringify({
        refresh_token: refreshToken,
        client_id:
            '378934482809-2pb4k84alqpudk4pmnhj0qaiec5i93lq.apps.googleusercontent.com',
        client_secret: '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',
        grant_type: 'refresh_token',
    })

    const response = await axios.post(
        'https://oauth2.googleapis.com/token',
        data,
        {
            headers: { 'Content-Type': 'application/json' },
        },
    )

    return response.data.access_token
}

export const sendEmail = async (
    toEmail: string,
    subject: string,
    ...messageContents: string[]
) => {
    const accessToken = await getAccessToken()

    const encodedMessage = emailEncoder(
        'Litta',
        'no-reply@litta.co',
        toEmail,
        subject,
        ...messageContents,
    )

    const res = await axios.post(
        `https://gmail.googleapis.com/gmail/v1/users/me/messages/send?access_token=${accessToken}`,
        {
            raw: encodedMessage,
        },
    )

    console.log(res.data)
}

const emailEncoder = (
    fromName: string,
    fromEmail: string,
    toEmail: string,
    subject: string,
    ...messageContents: string[]
) => {
    // Encode subject
    const encodedSubject = `=?utf-8?B?${window.btoa(subject)}?=`
    // Construct message
    const messageParts = [
        `From: ${fromName} <${fromEmail}>`,
        `To: ${getNameFromEmail(toEmail)} <${toEmail}>`,
        'Content-Type: text/html; charset=utf-8',
        'MIME-Version: 1.0',
        `Subject: ${encodedSubject}`,
        '',
        messageContents.join('<br>'),
    ]
    const message = messageParts.join('\n')

    console.log(message)

    // Base64 encode message
    const encodedMessage = window
        .btoa(message)
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '')

    return encodedMessage
}

const getNameFromEmail = (email: string) => {
    return (
        email.charAt(0).toUpperCase() + email.substring(1, email.indexOf('@'))
    )
}

export const getRefundPolicyDetails = async (firebaseId: string) => {
    const url = `${baseUrlGen2}/check_cancelation`
    const body = {
        data: {
            recordId: firebaseId,
        },
    }

    const { data } = await axios.post(url, body)
    const {
        isCancellable,
        isReschedulable,
        cancellationfee: fee,
        hours_diff: diff,
        cancellationQuote,
    } = data.result
    const mapped = {
        isCancellable: isCancellable,
        isReschedulable: isReschedulable,
        cancellationFee: fee,
        cancellationQuote: (cancellationQuote ?? null) as CancellationQuote | null,
        timeRemaining:
            diff < 24 ? `${diff} hours` : `${(diff / 24).toFixed(0)} days`,
    }
    return mapped
}

export const priceConverter = (price: number) => +(price / 100)?.toFixed(2)

export const calculateTotalAmount = (
    status: Record<string, any>[],
    statusType: string,
) => {
    const summarize = status.reduce((acc, st) => {
        if (st?.data && st?.type === statusType) {
            acc += st.data.object.amount
        } else if (st?.resource) {
            acc += st.resource.purchase_units[0].amount.value * 100
        } else if (st?.transactionAmount?.amount) {
            acc += Number(st.transactionAmount.amount) * 100
        }
        return acc
    }, 0)
    const priceConverted = priceConverter(summarize)
    return priceConverted
}

export const calculateTotalBankPaidAmount = (status: Record<string, any>[]) => {
    const summarize = status.reduce((acc, st) => {
        const raw = st?.transactionAmount?.amount
        if (raw == null || raw === '') {
            return acc
        }
        const n = Number(raw)
        return acc + (Number.isFinite(n) ? n : 0)
    }, 0)

    return summarize
}

export const cancelBookingRequest = async (
    recordId: string,
    total: number,
    reason: string | null,
    subReason: string | null,
    user: string,
    userEmail: string,
    cancellationFee: number,
) => {
    const url = `${baseUrlGen2}/cancel_my_collection`

    const response = await axios.post(url, {
        data: {
            recordId,
            REFUND_AMOUNT: total,
            CANCELLATION_FEE: cancellationFee,
            REFUND_REASON: reason,
            userEmail,
            SUB_REASON: subReason,
        },
    })

    await addKeyEvent(recordId, {
        type: 'rescheduleDate',
        user,
        timestamp: new Timestamp(new Date().getTime() / 1000, 0),
        source: 'Beehive',
        metadata: {
            message: `Cancellation requested for £${total} with ${
                reason ? `reason ${reason}` : 'no reason'
            }`,
        },
    })

    return response.status == 200
}

export const getUserDetails = async (userId: string) => {
    const endpoint = `https://api.litta.tech/v1/orders/user/${userId}/`
    const { data } = await axios.get(endpoint)

    return data?.name
}

export const changeCollectionDate = async (
    selectedDate: string,
    timeSlot: string | null,
    rescheduleReason: string | null,
    rescheduleSubReason: string | null,
    recordId: string,
    user: string,
    oldCollectionDate?: string | null,
    oldTimeslot?: string | null,
    by?: string | null,
) => {
    const endpoint = `${baseUrlGen2}/reschedule_collection`

    const data: Record<string, unknown> = {
        selectedDate,
        recordId,
        reschedule_reason: rescheduleReason,
        reschedule_sub_reason: rescheduleSubReason,
        old_collection_date: toPaddedDdMmYyyy(oldCollectionDate) ?? null,
        new_collection_date: toPaddedDdMmYyyy(selectedDate) ?? selectedDate,
        old_timeslot: oldTimeslot ?? null,
        new_timeslot: timeSlot ?? oldTimeslot ?? null,
        by: by ?? null,
    }

    if (timeSlot) {
        data.selectedSlot = timeSlot
        data.selected_slot_id = 'custom'
    }

    const { data: response } = await axios.post(endpoint, { data })

    await addKeyEvent(recordId, {
        type: 'rescheduleDate',
        user,
        timestamp: new Timestamp(Math.round(new Date().getTime() / 1000), 0),
        source: 'Beehive',
        metadata: {
            message: `Date rescheduled to ${selectedDate} ${
                timeSlot
                    ? `slot change to ${timeSlot}`
                    : 'with no timeslot change'
            } with ${
                rescheduleReason ? `reason ${rescheduleReason}` : 'no reason'
            }`,
        },
    })

    return response
}

export const getPayoutOfferType = (
    customerType: string,
    imageCleansed?: string,
    payoutCategory?: string,
    items?: string[],
) => {
    if (customerType == 'commercial') {
        return 'Load&Go'
    }

    console.log('Accepted - In line with service')
    console.log(imageCleansed)
    const isAcceptInLineWithService =
        imageCleansed == 'Accepted - In line with service'
    const isPayoutCategoryMatched =
        payoutCategory == 'single_item' ||
        payoutCategory == 'additional(s)_item_only'
    const isSingleSmallLoad =
        items?.length == 1 && items[0] == 'Load (Max 0.5yd³ / 10kgs)'

    console.log(
        isAcceptInLineWithService,
        isPayoutCategoryMatched,
        isSingleSmallLoad,
    )

    if (
        isAcceptInLineWithService ||
        isPayoutCategoryMatched ||
        isSingleSmallLoad
    ) {
        return 'Fixed Payout'
    }

    return 'Estimated Payout'
}

export const getBusinessOperationsUsers = async () => {
    const { data } = await axios.get(
        'https://litta-tech-api-gen2.web.app/v1/management/users/group/ugpuWLglTBEy8HDXo',
    )
    return data as GroupResponse
}

export const getPayoutCategoriesFromDbOrCache = async (
    storage?: Storage,
): Promise<PayoutCategory[] | null> => {
    if (!storage) {
        return await getPayoutCategories()
    }

    const cachedCategories = storage?.getItem('payoutCategories')
    if (cachedCategories) {
        const { ttl, data } = JSON.parse(cachedCategories)
        const date = ttl ? new Date(ttl) : new Date()

        if (new Date().getTime() < date.getTime()) {
            return data
        }
    }

    const data = await getPayoutCategories()

    // Set time to live for data for ttl
    const ttl = new Date()
    ttl.setDate(ttl.getDate() + 5)

    storage?.setItem(
        'payoutCategories',
        JSON.stringify({ ttl: ttl.toISOString(), data }),
    )
    return data
}

export const updateCleanseData = async (
    firebaseId: string,
    recordId: string | undefined,
    data: CleanseData,
    user: string,
    email: string,
    order: BookOrder | null | undefined,
) => {
    console.log('Beginning cleanse')

    // An SPA is set by ops and the carrier payout reads it, so cleanse must not replace it
    if (order?.payoutType === 'SPA') {
        data = { ...data, payoutType: 'SPA' }
    }

    const requiresTippingTicket = order
        ? !isExemptFromTippingTicket(order)
        : true

    const fields: Record<
        string,
        string | string[] | boolean | null | undefined
    > = {
        carrier_job_description: data.carrierJobDescription,
        team_size: data.teamSize,
        vehicle_type: data.vehicleType?.[0],
        image_cleansed: data.imageCleansed,
        payoutType: data.payoutType,
        requires_empty_van: data.requiresEmptyVan,
        tipping_ticket_status: requiresTippingTicket ? 'Required' : null,
        'Cleansed By': email,
        gender: data.userDetails?.gender,
        potentialBusiness: data?.potentialBusiness,
        hazardous_consignment: data?.isHazConsignmentNoteRequired
            ? 'Required'
            : 'Not Required',
    }

    Object.keys(fields).forEach(key =>
        fields[key] === undefined ? delete fields[key] : {},
    )

    const request = {
        baseId: 'appyuCUJQH2WF7JDj',
        tableId: 'tblYI3REdC4D6h1iu',
        records: {
            id: recordId,
            fields,
        },
    }

    if (!isStaging && recordId) {
        console.log('Adding to Airtable queue')
        await axios.post('https://api.litta.tech/v1/proxy/queue', request, {
            headers: { 'Content-Type': 'application/json' },
        })
    }

    console.log('Adding to Firebase')
    console.log(data)
    await addCleanseData(firebaseId, data, requiresTippingTicket)

    console.log('Adding key event')
    await addKeyEvent(firebaseId, {
        type: 'cleanse',
        user,
        timestamp: new Timestamp(new Date().getTime() / 1000, 0),
        source: 'Beehive',
        metadata: {
            message: `Cleansed job with payout type ${data.payoutType}`,
            request: JSON.stringify(request),
            fields: JSON.stringify(fields),
        },
    })
}

export const getLoadAndAdditionalItemsFromCache = async (
    storage?: Storage,
): Promise<{ loads: ServicePricing[]; items: ServicePricing[] }> => {
    if (!storage) {
        return await getLoadAndAdditionalItems()
    }

    const cachedItems = storage?.getItem('load-additional-items')
    if (cachedItems) {
        const { ttl, data } = JSON.parse(cachedItems)
        const date = ttl ? new Date(ttl) : new Date()

        if (new Date().getTime() < date.getTime()) {
            return data
        }
    }

    const data = await getLoadAndAdditionalItems()

    // Set time to live for data for ttl
    const ttl = new Date()
    ttl.setDate(ttl.getDate() + 5)

    try {
        storage?.setItem(
            'load-additional-items',
            JSON.stringify({ ttl: ttl.toISOString(), data }),
        )
    } catch (e) {
        console.log(
            'CANNOT STORE LOADS AND ADDITIONAL ITEMS, EXCEEDED STORAGE QUOTA',
        )
    }
    return data
}

export const uploadImages = async (documentId: string, images: string[]) => {
    const endpoint = `${baseUrlGen2}/save_images`
    const data = {
        recordId: documentId,
        images,
    }

    await axios.post(endpoint, { data })
}

export const getAutocompleteAddress = async (term: string) => {
    const endpoint = `https://europe-west2-litta-technology.cloudfunctions.net/address/autocomplete/${encodeURIComponent(
        term.trim(),
    )}?all=true`
    const { data } = await axios.get(endpoint)

    return data as GetAddressIOAutocompleteResponse
}

export const getAddress = async (term: string) => {
    const endpoint = `https://europe-west2-litta-technology.cloudfunctions.net/address/find/${encodeURIComponent(
        term.trim(),
    )}?expand=true`
    const { data } = await axios.get(endpoint)

    return data as GetAddressIOResponse
}

export const getAddressById = async (id: string) => {
    const endpoint = `https://europe-west2-litta-technology.cloudfunctions.net/address/get/${id}`
    const { data } = await axios.get(endpoint)

    return data as AddressDetails
}

export const getActiveServicePricingItems = async (
    storage?: Storage,
): Promise<ServicePricing[]> => {
    if (!storage) {
        return await getActiveServicePricingItemsFromDb()
    }

    const cachedItems = storage?.getItem('active-load-additional-items')
    if (cachedItems) {
        const { ttl, data } = JSON.parse(cachedItems)
        const date = ttl ? new Date(ttl) : new Date()

        if (new Date().getTime() < date.getTime()) {
            return data
        }
    }

    const data = await getActiveServicePricingItemsFromDb()

    // Set time to live for data for ttl
    const ttl = new Date()
    ttl.setDate(ttl.getDate() + 5)

    try {
        storage?.setItem(
            'active-load-additional-items',
            JSON.stringify({ ttl: ttl.toISOString(), data }),
        )
    } catch (e) {
        console.log(
            'CANNOT STORE LOADS AND ADDITIONAL ITEMS, EXCEEDED STORAGE QUOTA',
        )
    }
    return data
}

export const createAdditionalBooking = async (
    payload: Record<string, unknown>,
): Promise<AdditionaLBookingResult> => {
    const endpoint = isQA
        ? `${baseUrlGen2}/mlc_additional_booking_qa`
        : `${baseUrlGen2}/mlc_additional_booking`
    const requestData = { data: payload }

    const { data } = await axios.post(endpoint, requestData)

    return data
}

export const createAdditionalPayment = async (
    payload: Record<string, unknown>,
) => {
    const endpoint = `${baseUrlGen2}/additional_payment`
    const requestData = { data: payload }

    const { data } = await axios.post(endpoint, requestData)

    return data
}

export const checkTotalPrice = async (
    items: ServicePricing[],
    postcode?: string,
    date?: Date | string,
    timeslot?: Record<string, unknown>,
    userId?: string,
    manualAdjustment?: number,
    enquiryType?: string,
    useCustomerPriceWithoutFee?: boolean,
    createdAt?: Date,
    additionalFees?: Array<{
        name: string
        baseSku: string
        baseSkuWithoutIdentifier: string
        count: number
    }>,
    dismantledItems?: Array<{ id: string; count: number }>,
): Promise<number> => {
    const pricingFunctionUrl = isQA
        ? 'https://pricing-qa-d2gswiyoaa-nw.a.run.app'
        : isStaging
          ? 'https://europe-west2-staging-9c37f.cloudfunctions.net/pricing'
          : 'https://pricing-d2gswiyoaa-nw.a.run.app'

    const mappedItems = items.map(item => ({
        id: item.record_id,
        count: item.count ?? 1,
    }))

    const fees =
        additionalFees && additionalFees.length > 0
            ? mapToPricingFees(additionalFees)
            : []

    const { data } = await axios.post(pricingFunctionUrl, {
        items: mappedItems,
        ...(fees.length > 0 && { fees }),
        ...(dismantledItems?.length && { dismantledItems }),
        postcode,
        date: date
            ? typeof Date === 'string'
                ? date
                : (date as Date).toISOString()
            : undefined,
        timeslot: timeslot ? timeslot : undefined,
        userId: userId ? userId : undefined,
        manualAdjustment: manualAdjustment ? manualAdjustment : undefined,
        enquiryType:
            enquiryType === 'businessPro'
                ? 'business'
                : (enquiryType ?? undefined),
        createdAt: createdAt ? createdAt : new Date(),
    })

    const { customerPrice, customerPriceWithoutFee } = data.data.result
    const price = useCustomerPriceWithoutFee
        ? customerPriceWithoutFee
        : customerPrice

    return price
}

export const checkTotalPriceFromRecordId = async (
    id: string,
    partnerId: string,
): Promise<PricingResponse> => {
    const pricingFunctionUrl = isQA
        ? 'https://pricing-qa-d2gswiyoaa-nw.a.run.app'
        : isStaging
          ? 'https://europe-west2-staging-9c37f.cloudfunctions.net/pricing'
          : 'https://pricing-d2gswiyoaa-nw.a.run.app'

    const { data } = await axios.post(pricingFunctionUrl, {
        id,
        partnerReference: partnerId,
    })

    return data
}

export const updateItems = async (
    firebaseId: string,
    items: Item[],
    user: string,
    additionalFees?: Array<{
        name: string
        baseSku: string
        baseSkuWithoutIdentifier: string
        count: number
    }>,
) => {
    const endpoint = isQA
        ? `${baseUrlGen2}/upload_load_qa`
        : `${baseUrlGen2}/upload_load`

    const data = {
        firebase_id: firebaseId,
        items,
        user,
        ...(additionalFees && additionalFees.length > 0 && { additionalFees }),
    }
    const response = await axios.post(endpoint, { data })

    return response
}

export const getStartAndEndDate = (offset: number | null, mode: Mode) => {
    const startDate = new Date()
    const endDate = new Date()

    if (offset && offset !== 0) {
        if (mode !== 'rolling' && mode !== 'month') {
            ;[startDate, endDate].forEach(date =>
                date.setDate(date.getDate() + (offset ?? 0)),
            )
        } else if (mode === 'month') {
            startDate.setMonth(startDate.getMonth() + offset, 1)
            endDate.setMonth(endDate.getMonth() + offset + 1, 0)
        } else {
            if (offset < 0) {
                startDate.setDate(startDate.getDate() + offset)
            } else {
                endDate.setDate(endDate.getDate() + offset)
            }
        }
    }

    startDate.setHours(0, 0, 0, 0)
    endDate.setHours(23, 59, 59, 999)

    return [Timestamp.fromDate(startDate), Timestamp.fromDate(endDate)]
}

type ConditionalOperator =
    '<' | '<=' | '=' | '>' | '>=' | '!=' | 'contains' | 'arrayObjectContains'

export const checkCondition = <T>(
    value: T,
    condition: T,
    operator: ConditionalOperator,
) => {
    switch (operator) {
        case '>':
            return value > condition
        case '>=':
            return value >= condition
        case '<':
            return value < condition
        case '<=':
            return value <= condition
        case '=':
            return value == condition
        case '!=':
            return value != condition
        case 'contains':
            return (
                (value as Array<T> | string)?.includes(
                    condition as string & T,
                ) ?? false
            )
        default:
            return false
    }
}

const CDN_BASE_URL = import.meta.env.VITE_CDN_BASE_URL
const CDN_BASE_URL_STAGING = import.meta.env.VITE_CDN_BASE_URL_STAGING

export const getCdnBaseUrl = (isStaging?: boolean) => {
    const url = isStaging ? CDN_BASE_URL_STAGING : CDN_BASE_URL
    if (!url) {
        console.warn(
            `CDN base URL not configured for ${isStaging ? 'staging' : 'production'}`,
        )
    }
    return url
}

export const purgeCachedAPI = async (
    collectionName: string,
    whereQuery?: string,
    isStaging?: boolean,
) => {
    const baseUrl = `${getCdnBaseUrl(isStaging)}/${collectionName}`
    try {
        await axios.request({
            method: 'PURGE',
            url: whereQuery ? `${baseUrl}?where=${whereQuery}` : baseUrl,
        })
        return true
    } catch (err) {
        return false
    }
}

export const getFromCachedAPI = async <T>(
    collectionName: string,
    whereQuery?: string,
    isStaging?: boolean,
) => {
    const baseUrl = `${getCdnBaseUrl(isStaging)}/${collectionName}`
    const response = await axios.get(
        whereQuery ? `${baseUrl}?where=${whereQuery}` : baseUrl,
    )
    return response.data.result as (T & { id: string })[]
}

export const mapSkusToRecord = async (
    skus?: { id: string; count: number }[],
) => {
    if (!skus) return []
    const loads = await getFromCachedAPI<PricingSchedule>(
        'pricing_schedule',
        'is_main_payout,==,true',
    )
    const items = await getFromCachedAPI<PricingSchedule>(
        'pricing_schedule',
        'is_additional,==,true',
    )
    const records = [...loads, ...items]
    return skus.map(obj => {
        const { id, count } = obj
        const record = records.find(record => record.record_id == id)
        const object = {
            ...record,
            count,
            adjustment: 0,
            final: (record?.price_ex_vat ?? 0) * count,
        }
        return object
    })
}

export const updateAirtable = async (
    baseId: string,
    tableId: string,
    recordId: string,
    fields: Record<string, unknown>,
) => {
    const request = {
        baseId,
        tableId,
        records: {
            id: recordId,
            fields,
        },
    }

    console.log('Adding to Airtable queue')
    await axios.post('https://api.litta.tech/v1/proxy/queue', request, {
        headers: { 'Content-Type': 'application/json' },
    })
}

export const generateJobCard = async (
    data: BookOrder,
    userName: string,
    commercialAccount: CommercialAccount | null,
    source = '/postreview',
): Promise<string> => {
    const url =
        'https://us-central1-book-litta-co.cloudfunctions.net/documents-app/v1/documents/create?hasPayload=true&type=job_card_commercial'
    const payload = {
        documentId: data.recordId,
        reference: data.LTA,
        address: data.address.replace(/,/g, ',\n'),
        collectionDate: data.selectedDate.split('-').join('/'),
        completionTime: data.appJobDetails?.completedAt
            .toDate()
            .toLocaleTimeString('en-GB', { timeZone: 'Europe/London' }),
        carrierSignature:
            'https://firebasestorage.googleapis.com/v0/b/litta-technology.appspot.com/o/web_images%2Flitta_signature2.svg?alt=media&token=2cb751e5-9828-487a-ab10-0f684a406dd8&_gl=1*14em7hc*_ga*MTgxMjMxMTU2NC4xNjU1MzIzODEz*_ga_CW55HF8NVT*MTY4NjIyNjM1MC44Ni4xLjE2ODYyMjY1NDIuMC4wLjA.',
        customerSignature: data.appJobDetails?.signatureImage ?? '',
        client: data.tracked_customer?.[0] ?? '',
        service: data.items
            .map(item => `${item.count ?? 1}x ${item['LITTA Service']}`)
            .join(', '),
        sicCodes: commercialAccount?.sicCode ?? '',
        siteContact: data.userDetails.on_site_contact ?? '',
        allImagesBefore: data.appJobDetails?.allImagesBefore ?? [],
        allImagesAfter: data.appJobDetails?.allImagesAfter ?? [],
    }

    const response = await axios.post(url, payload)
    const { customUrl } = response.data

    if (!customUrl) {
        throw new Error('Cannot get link to job card from server')
    }

    await updateFirebaseDocument('book_orders', data.recordId, {
        job_card: customUrl,
        keyEventsTimeline: arrayUnion({
            type: 'generateJobCard',
            user: userName,
            timestamp: new Date(),
            source,
            title: 'Generated job card',
            metadata: {
                message: `Job card created`,
            },
        }),
    })
    return customUrl
}

export const generateWasteTransferNote = async (
    data: Record<string, unknown>,
    userName: string,
    isSkipUpdate?: boolean,
) => {
    if (data.weight) data.weight = parseInt(data.weight as string)

    const url =
        'https://us-central1-book-litta-co.cloudfunctions.net/documents-app/v1/documents/create?type=waste_transfer_note_commercial&hasPayload=true'
    const response = await axios.post(url, data)
    const { customUrl } = response.data

    if (!customUrl) {
        throw new Error(
            'Cannot generate waste transfer note. Unable to retrieve link from server.',
        )
    }

    if (isSkipUpdate) {
        return customUrl
    }

    await updateFirebaseDocument('book_orders', data.documentId as string, {
        disposalSite: data?.disposalSite ?? null,
        disposalSiteLicense: data?.disposalSiteLicense ?? null,
        weight: data?.weight ?? null,
        waste_transfer_note: customUrl,
        wasteTransferNoteUpdateCount: increment(1),
        keyEventsTimeline: arrayUnion({
            type: 'generateWasteTransferNote',
            user: userName,
            timestamp: new Date(),
            source: '/postreview',
            title: 'Generated waste transfer note',
            metadata: {
                message: `Waste transfer note created`,
            },
        }),
    })
}

export const sendDocumentEmail = async (
    order: BookOrder,
    emailAddresses: string[],
    documentType: 'wasteTransferNote' | 'jobCard',
    userName: string,
    source = '/postreview',
) => {
    const promises = []
    const isCommercial =
        order.enquiry_type === 'commercial' ||
        order.client_type === 'commercial'

    let subject = `Litta ${
        documentType === 'wasteTransferNote'
            ? 'Waste Transfer Note'
            : 'Job Card'
    } ${order.LTA}`

    if (order.purchase_order) {
        subject += ` - ${order.purchase_order}`
    }

    for (const emailAddress of emailAddresses) {
        const message = {
            from: isCommercial ? 'commercial' : 'no-reply',
            fromName: 'Litta',
            to: emailAddress,
            subject,
            messageContents: [
                'Dear Customer',
                '',
                `Please find attached your Litta ${
                    documentType === 'jobCard'
                        ? 'Job Card'
                        : 'Waste Transfer Note'
                }`,
                '',
                'We appreciate your continuous support. For any queries, please reach out to your relationship manager or the commercial team.',
                '',
                documentType === 'jobCard'
                    ? order.job_card
                    : order.waste_transfer_note,
                '',
                'Many thanks',
                'Litta Compliance Team',
                `${isCommercial ? 'commercial' : 'hi'}@litta.co`,
                '0330 828 1287',
            ],
        } as Record<string, unknown>

        if (isCommercial) {
            message.from = 'commercial'
        } else {
            message.fromAccount = 'hi'
        }

        const url = 'https://api.litta.tech/v2/gmail/send'

        promises.push(axios.post(url, message))
    }

    const firebaseUpdateBody = {
        [documentType === 'jobCard'
            ? 'jobCardEmailed'
            : 'isWasteTransferEmailed']: true,
        keyEventsTimeline: arrayUnion({
            type: 'sendEmail',
            user: userName,
            timestamp: new Date(),
            source,
            title: 'Email sent',
            metadata: {
                message: `Sent email for ${camelCaseToWords(
                    documentType,
                )} to ${emailAddresses.join(', ')}`,
            },
        }),
    }

    const promise = updateFirebaseDocument(
        'book_orders',
        order.recordId,
        firebaseUpdateBody,
    )
    console.log(firebaseUpdateBody)

    promises.push(promise)

    try {
        console.log('Sending job card')
        const results = await Promise.all(promises)
        console.log(results)
        return [true, null]
    } catch (err) {
        return [false, err]
    }
}

export const sendWhatsapp = async (
    order: BookOrder,
    documentType: 'wasteTransferNote' | 'jobCard',
    userName: string,
    source = '/postreview',
) => {
    const number =
        order.userDetails.mobilenumber ?? order.userDetails.additional_mobile

    if (!number) {
        return [true, 'Could not find number to send message to.']
    }

    const message = `Please find attached your Litta ${
        documentType === 'jobCard' ? 'Job Card' : 'Waste Transfer Note'
    }
    
We appreciate your continuous support. For any queries, please reach out to your relationship manager or the commercial team.

${documentType === 'jobCard' ? order.job_card : order.waste_transfer_note}

Many thanks,
Litta Compliance Team`

    const url = `https://litta-tech-api-gen2.web.app/v1/messages/send`
    const payload = {
        id: order.recordId,
        reference: order.LTA,
        phone: number,
        message,
    }

    const promises: Promise<any>[] = [axios.post(url, payload)]

    const promise = updateFirebaseDocument('book_orders', order.recordId, {
        keyEventsTimeline: arrayUnion({
            type: 'whatsappSent',
            user: userName,
            timestamp: new Date(),
            source,
            title: 'Whatsapp sent',
            metadata: {
                message: `Job card WhatsApp sent for ${camelCaseToWords(
                    documentType,
                )}`,
                number,
            },
        }),
    })
    promises.push(promise)

    try {
        await Promise.all(promises)
        return [true, null]
    } catch (err) {
        return [false, err]
    }
}

const getExternalTimeslot = (timeslot?: string): string => {
    if (!timeslot) return 'N/A'

    try {
        const [start, end] = timeslot.split(' - ')
        const [startHour, startMinute] = start.split(':').map(Number)
        const [endHour, endMinute] = end.split(':').map(Number)

        let adjustedEndHour = endHour - 1
        let adjustedEndMinute = endMinute

        // Cap the end time at 19:00
        if (adjustedEndHour >= 19) {
            adjustedEndHour = 19
            adjustedEndMinute = 0
        }

        // Ensure the adjusted end time is not before the start time
        if (
            adjustedEndHour < startHour ||
            (adjustedEndHour === startHour && adjustedEndMinute < startMinute)
        ) {
            return timeslot // Return original timeslot if the adjusted end time is before the start time
        }

        const newEnd = `${adjustedEndHour
            .toString()
            .padStart(2, '0')}:${adjustedEndMinute.toString().padStart(2, '0')}`
        return `${start} - ${newEnd}`
    } catch (error) {
        console.log(error)
        return 'N/A'
    }
}

export const getDateWithTimeslot = (date: Date, timeslot: string | null) => {
    // Check if dash and colon exist
    const isCorrectFormat = ['-', ':'].every(char => timeslot?.includes(char))
    const timeslotBeginning = timeslot
        ? isCorrectFormat
            ? Number(timeslot.split(' - ')[0].split(':')[0])
            : 7
        : 7

    // Set time of selected date and return
    date.setHours(
        Number.isNaN(timeslotBeginning) ? 7 : timeslotBeginning,
        0,
        0,
        0,
    )
    return date
}

const convertToReadableText = (arr: (DismantleObject | DisconnectObject)[]) =>
    arr
        .map(
            ({ count, litta_service_linked }) =>
                `${count}x ${litta_service_linked}`,
        )
        .join(', ')

const TEAMS_CACHE_TTL_MS = 5 * 60 * 1000

let cachedTeams: Team[] | null = null
let cachedTeamsAtMs = 0
let teamsInflight: Promise<Team[]> | null = null

const TEAMS_ENDPOINT = 'https://data-drivers-d2gswiyoaa-uc.a.run.app'

const refreshTeams = (): Promise<Team[]> => {
    const request = (async () => {
        const { data } = await traceAsync('  GET teams', () =>
            axios.get(TEAMS_ENDPOINT),
        )
        const teams = data.data as Team[]
        cachedTeams = teams
        cachedTeamsAtMs = Date.now()
        return teams
    })()

    teamsInflight = request
    request
        .catch(() => undefined)
        .finally(() => {
            if (teamsInflight === request) {
                teamsInflight = null
            }
        })

    return request
}

/**
 * The endpoint returns every team with its drivers, postcode coverage and
 * capacity, and callers that remount often — the offer modal rebuilds on every
 * open — were refetching all of it each time. Each caller gets its own array so
 * that one of them calling markRaw cannot strip reactivity for the others.
 *
 * Served stale-while-revalidate: this is a cold-starting Cloud Run service that
 * has been measured at 7s, and the offer modal cannot render its carrier list
 * until it resolves. Once we have any teams at all, refresh in the background
 * rather than making a caller wait. Pass `fresh` to force a blocking refetch.
 */
export const getTeams = async (options?: { fresh?: boolean }) => {
    if (options?.fresh) {
        traceEvent('teams', 'forced refresh')
        return [...(await refreshTeams())]
    }

    if (cachedTeams) {
        const isStale = Date.now() - cachedTeamsAtMs >= TEAMS_CACHE_TTL_MS
        if (isStale && !teamsInflight) {
            void refreshTeams().catch(() => undefined)
        }
        traceEvent('teams', isStale ? 'cache hit (revalidating)' : 'cache hit')
        return [...cachedTeams]
    }

    if (teamsInflight) {
        traceEvent('teams', 'awaiting inflight')
        return [...(await teamsInflight)]
    }

    traceEvent('teams', 'cache MISS')
    return [...(await refreshTeams())]
}

export const dateToString = (day: number, month: number, year: number) => {
    const padNumber = (num: number) => {
        return num.toString().padStart(2, '0')
    }

    const newDay = padNumber(day)
    const newMonth = padNumber(month)

    return `${year}-${newMonth}-${newDay}`
}

export const createGatewaySlackBlocks = (details: SlackMessageDetails) => {
    // Treat missing active flag as active so legacy docs without the field
    // still appear in the message; filter out groups with no postcodes since
    // they have no operational effect.
    const surgesWithPostcodes = details.surgedPostcodeGroups.filter(
        s => s.postcodeGroups.length > 0,
    )
    const activeSurges = surgesWithPostcodes.filter(s => s.active !== false)
    const inactiveSurges = surgesWithPostcodes.filter(s => s.active === false)

    const formatGroup = (group: string): string => {
        const count = details.postcodeCounts?.[group]
        return count ? `${group}(${count.utilised}/${count.total})` : group
    }

    const renderSurgeBlock = (surge: Surge, suffix = ''): any => ({
        type: 'section',
        text: {
            type: 'mrkdwn',
            text: `*Surge Group #${surge.surgeGroupId} [${surge.surgeLevel?.toUpperCase()}]*${suffix} - ${surge.postcodeGroups
                .map(formatGroup)
                .join(', ')}`,
        },
    })

    const activeSurgeBlocks = activeSurges.map(s => renderSurgeBlock(s))
    const inactiveSurgeBlocks = inactiveSurges.map(s =>
        renderSurgeBlock(s, ' _(inactive)_'),
    )
    const inactiveHeader =
        inactiveSurges.length > 0
            ? [
                  {
                      type: 'section',
                      text: {
                          type: 'plain_text',
                          text: `${inactiveSurges.length} surge ${
                              inactiveSurges.length === 1 ? 'group' : 'groups'
                          } configured but currently inactive.`,
                          emoji: true,
                      },
                  },
              ]
            : []

    const activeSurgedPostcodeCount = activeSurges.reduce(
        (acc, s) => acc + s.postcodeGroups.length,
        0,
    )

    const blocks = [
        {
            type: 'header',
            text: {
                type: 'plain_text',
                text: `Gateway update for ${details.date.toLocaleDateString(
                    'en-GB',
                )}`,
                emoji: true,
            },
        },
        {
            type: 'section',
            text: {
                type: 'mrkdwn',
                text: `Surges/closed areas have been updated by ${
                    details.name
                } at ${new Date().toLocaleString(
                    'en-GB',
                )} for *${details.date.toLocaleDateString('en-GB')}*`,
            },
        },
        {
            type: 'divider',
        },
        {
            type: 'section',
            text: {
                type: 'mrkdwn',
                text: `*${
                    details.noClosedPostcodeGroups === 0
                        ? 'No'
                        : details.noClosedPostcodeGroups
                }* postcode ${
                    details.noClosedPostcodeGroups === 1 ? 'group' : 'groups'
                } closed.${
                    details.noClosedPostcodeGroups === 0
                        ? ''
                        : ' ' +
                          details.closedPostcodeGroups
                              .map(formatGroup)
                              .join(', ')
                }`,
            },
        },
        {
            type: 'divider',
        },
        {
            type: 'section',
            text: {
                type: 'plain_text',
                text: `${
                    activeSurgedPostcodeCount === 0
                        ? 'No'
                        : activeSurgedPostcodeCount
                } postcode ${
                    activeSurgedPostcodeCount === 1 ? 'group' : 'groups'
                } surged across ${activeSurges.length} active surge ${
                    activeSurges.length === 1 ? 'group' : 'groups'
                }.`,
                emoji: true,
            },
        },
        ...activeSurgeBlocks,
        ...inactiveHeader,
        ...inactiveSurgeBlocks,
        {
            type: 'divider',
        },
        {
            type: 'section',
            text: {
                type: 'mrkdwn',
                text: `*${
                    details.globalSurgeActive
                        ? details.globalSurgeLevel.toUpperCase()
                        : 'No'
                }* global surge level has been set.`,
            },
        },
        {
            type: 'divider',
        },
        {
            type: 'context',
            elements: [
                {
                    type: 'plain_text',
                    text: 'Any issues, contact tech team. Localised surges (surges set by specific postcode groups) will always take priority over the set global surge when charging the customer.',
                    emoji: true,
                },
            ],
        },
        {
            type: 'actions',
            elements: [
                {
                    type: 'button',
                    text: {
                        type: 'plain_text',
                        text: 'Open in Gateway',
                        emoji: true,
                    },
                    style: 'primary',
                    value: 'open-in-gateway',
                    url: `${window.location.origin}/gateway`,
                    action_id: 'open-in-gateway',
                },
            ],
        },
    ]

    return blocks
}

export const getAllFees = async (
    startDate: Date,
    endDate: Date,
    postcode: string,
): Promise<Fee[]> => {
    const baseUrl = isStaging
        ? 'https://europe-west2-staging-9c37f.cloudfunctions.net/getCalenderPrice'
        : 'https://europe-west2-book-litta-co.cloudfunctions.net/getCalenderPrice'

    const startDateYear = startDate.getFullYear()
    const startDateMonth = startDate.getMonth() + 1
    const startDateDay = startDate.getDate()
    const startDateString = `${startDateYear}-${startDateMonth}-${startDateDay}`

    const endDateYear = endDate.getFullYear()
    const endDateMonth = endDate.getMonth() + 1
    const endDateDay = endDate.getDate()
    const endDateString = `${endDateYear}-${endDateMonth}-${endDateDay}`

    const url = `${baseUrl}?postcode=${postcode}&from=${startDateString}&to=${endDateString}`

    const response = await axios.get(url)
    return response.data
}

export const createPayloadForQuoteCreation = (data: Record<string, any>) => {
    const isBusinessPro = data?.enquiryType === 'businessPro'

    const conditionalFields = {
        ...(!isBusinessPro && {
            enquiry_type: data?.enquiryType,
        }),
        ...(isBusinessPro && {
            enquiry_type: 'business',
            client_type: data?.enquiryType,
        }),
        ...(isBusinessPro && {
            tracked_customer: [
                data?.selectedBusinessProClient?.companyLegalName,
            ],
        }),
        ...(data.billingName && { billing_name: data.billingName }),
        ...(data.formattedBillingAddress && {
            billing_address: data.formattedBillingAddress,
        }),
    }

    const payload = {
        address_details: data?.addressDetails ?? {},
        answers: [],
        business_name: data.businessName,
        description: data?.description,
        images: data?.images,
        litta_comments: '',
        notes_to_customer: data?.customerNotes,
        selectedDate: data.date ? formatDate(data.date) : '',
        selectedSlot: data?.selectedTimeslot?.timeslot ?? '',
        selected_slot_id: data?.selectedTimeslot?.recordId ?? '',
        is_live_slot: false,
        userDetails: {
            email: data?.email,
            mobilenumber: data?.number,
            name: data?.name,
            ...(data?.siteContact?.name && {
                on_site_contact: data.siteContact.name,
            }),
            ...(data?.siteContact?.number && {
                additional_mobile: data.siteContact.number,
            }),
            ...(data?.siteContact?.email && {
                onSiteContactEmail: data.siteContact.email,
            }),
        },
        TIME_SLOT_FEE: data?.selectedTimeslot?.price ?? 0,
        selectedLastBookingTime: '',
        address: data?.address,
        booking_type: null,
        coupon: data?.selectedDiscountCode,
        disconnect: data?.disconnect?.filter(
            (item: DisconnectObject) => item.count !== 0,
        ),
        dismantle: data?.dismantle?.filter(
            (item: DismantleObject) => item.count !== 0,
        ),
        items: data.selectedServices,
        ...(data.selectedAdditionalFees &&
            data.selectedAdditionalFees.length > 0 && {
                additionalFees: data.selectedAdditionalFees.map(
                    (fee: {
                        name: string
                        baseSku: string
                        baseSkuWithoutIdentifier: string
                        count: number
                    }) => ({
                        name: fee.name,
                        baseSku: fee.baseSku,
                        baseSkuWithoutIdentifier: fee.baseSkuWithoutIdentifier,
                        count: fee.count,
                    }),
                ),
            }),
        leadTime: '',
        manual_adjustment: data.manualAdjustment,
        owner: data?.selectedSalesOwner?.userId,
        referral_indicator: isBusinessPro
            ? data?.selectedBusinessProClient?.externalAccountRef
            : 'quotebuilder',
        status: 'created',
        send_quote: false,
        platform: 'quotebuilder',
        quoteBasedOn: data.quoteBasedOn,
        whatsappOptIn: data.whatsappStatus === 'Opt In' ? true : false,
        waiveBookingFee: data.bookingFeeRemoved ?? false,
        ...conditionalFields,
    }

    return payload
}

const mapping = {
    email: 'userDetails.email',
    number: 'userDetails.mobilenumber',
    name: 'userDetails.name',
    'siteContact.name': 'userDetails.on_site_contact',
    'siteContact.number': 'userDetails.additional_mobile',
    'siteContact.email': 'userDetails.onSiteContactEmail',
    'address.address': 'address',
    addressDetails: 'address_details',
    description: 'description',
    customerNotes: 'notes_to_customer',
    selectedServices: 'items',
    selectedAdditionalFees: 'additionalFees',
    date: 'selectedDate',
    enquiryType: 'enquiry_type',
    'selectedTimeslot.recordId': 'selected_slot_id',
    'selectedTimeslot.timeslot': 'selectedSlot',
    'selectedTimeslot.price': 'TIME_SLOT_FEE',
    'selectedSalesOwner.userId': 'owner',
    images: 'images',
    businessName: 'business_name',
    selectedDiscountCode: 'coupon',
    bookingFeeRemoved: 'waiveBookingFee',
    manualAdjustment: 'manual_adjustment',
    selectedUsageBucket: 'lead_time',
    selectedFailReason: 'fail_reason',
    dismantle: 'dismantle',
    disconnect: 'disconnect',
    selectedSalesStatus: 'sales_status',
    quoteBasedOn: 'quoteBasedOn',
    whatsappStatus: 'whatsappOptIn',
    billingName: 'billing_name',
    formattedBillingAddress: 'billing_address',
} as Record<string, string>

const formatDate = (date: Date): string => {
    const padZero = (num: number): string => (num < 10 ? `0${num}` : `${num}`)

    const month = padZero(date.getMonth() + 1) // getMonth() returns 0-11, so we add 1
    const day = padZero(date.getDate())
    const year = date.getFullYear()

    return `${day}-${month}-${year}`
}

const flattenObject = (
    obj: Record<string, any>,
    parentKey: string = '',
    result: Record<string, any> = {},
): Record<string, any> => {
    Object.keys(obj).forEach(key => {
        const newKey = parentKey ? `${parentKey}.${key}` : key
        if (
            typeof obj[key] === 'object' &&
            !Array.isArray(obj[key]) &&
            obj[key] !== null
        ) {
            result[newKey] = obj[key] // Add the original nested object
            flattenObject(obj[key], newKey, result)
        } else {
            result[newKey] = obj[key]
        }
    })
    return result
}

const dotNotationToObject = (
    dotNotationObj: Record<string, any>,
): Record<string, any> => {
    const result: Record<string, any> = {}

    Object.keys(dotNotationObj).forEach(key => {
        const value = dotNotationObj[key]
        const keys = key.split('.')

        keys.reduce((acc, curr, idx) => {
            if (!acc[curr]) {
                acc[curr] = {}
            }
            if (idx === keys.length - 1) {
                acc[curr] = value
            }
            return acc[curr]
        }, result)
    })

    return result
}

const customIsEqual = (value1: any, value2: any): boolean => {
    if (value1 === undefined && value2 === null) return true
    if (value1 === null && value2 === undefined) return true
    return isEqual(value1, value2)
}

export const createPayloadForQuoteUpdate = (
    data: Record<string, any>,
    documentData: BookOrder,
    internalUse: boolean = false,
) => {
    const changes = {} as Record<string, any>

    const isBusinessPro = data?.enquiryType === 'businessPro'

    const conditionalFields = {
        ...(!isBusinessPro && {
            enquiry_type: data?.enquiryType,
        }),
        ...(isBusinessPro && {
            enquiry_type: 'business',
            client_type: data?.enquiryType,
        }),
        ...(isBusinessPro && {
            tracked_customer: [
                data?.selectedBusinessProClient?.companyLegalName,
            ],
        }),
    }

    const { documentData: d, ...allOtherData } = data

    const finalData = {
        ...allOtherData,
        ...conditionalFields,
    }

    for (const [key, value] of Object.entries(flattenObject(finalData))) {
        const keyToUpdate = mapping[key]
        if (!keyToUpdate) continue

        let existingValue = get(documentData, keyToUpdate)
        let actualValue = value

        if (key === 'date' && actualValue && !internalUse) {
            actualValue = formatDate(actualValue)
            console.log(`date existingValue`, value)
            console.log(`date value`, value)
            console.log(`date actualValue`, actualValue)
        }

        if (key === 'whatsappStatus') {
            actualValue = value === 'Opt In' ? true : false
        }

        if (
            key === 'enquiryType' &&
            documentData.client_type === 'businessPro'
        ) {
            continue
        }

        if (
            (key === 'addressDetails' || key == 'address_details') &&
            !actualValue
        ) {
            actualValue = {}
        }

        if (key === 'selectedUsageBucket' && actualValue) {
            actualValue = (value as UsageBuckets).options
        }

        if (key === 'selectedFailReason' && actualValue) {
            actualValue = (value as FailReasons).failed_reason
        }

        if (['disconnect', 'dismantle'].includes(key) && actualValue) {
            actualValue = actualValue.filter(
                (item: DisconnectObject | DismantleObject) => item.count > 0,
            )
        }

        if (
            [
                'selectedDate',
                'selectedLastBookingTime',
                'selectedSlot',
                'selected_slot_id',
            ].includes(keyToUpdate) &&
            !actualValue
        ) {
            actualValue = ''
        }

        if (keyToUpdate === 'address') {
            console.log('actualValue address', actualValue)
        }

        const areValuesEqual = customIsEqual(existingValue, actualValue)

        if (!areValuesEqual) {
            console.log(
                `Adding key ${key}/${keyToUpdate} to changes with value ${actualValue}`,
            )
            changes[keyToUpdate] = actualValue
        }
    }

    const object = {
        recordId: documentData.recordId,
        method: 'PATCH',
        status: 'quotebuilder_updated',
        is_live_slot: false,
        ...dotNotationToObject(changes),
    }

    console.log('object', object)
    return object
}

export const sendQuoteFromBackend = async (
    documentId: string,
    name: string,
    email: string,
    selectedSalesOwner: SalesOwner | null,
) => {
    const payload = {
        recordId: documentId,
        method: 'PATCH',
        send_quote: true,
        status: 'quotebuilder_updated',
        triggeredByName: name,
        triggeredByEmail: email,
    } as Record<string, unknown>

    if (selectedSalesOwner) {
        payload.owner = selectedSalesOwner.userId
    }

    const endpoint = isStaging
        ? 'https://europe-west2-staging-9c37f.cloudfunctions.net/quoteV2'
        : isQA
          ? 'https://europe-west2-book-litta-co.cloudfunctions.net/quoteV2_qa'
          : 'https://europe-west2-book-litta-co.cloudfunctions.net/quoteV2'

    try {
        await axios.post(endpoint, { data: payload })
        return true
    } catch (err) {
        return false
    }
}

export interface UseCalculateTotalWithCouponsProps {
    dayFee?: number
    timeSlotFee?: number
    manualAdjustment?: number
    quoteAmount: number
    coupon: PromoCode | null
}

const amountToStripeSubUnit = (amount: number) => Math.round(amount * 100)

export const getTotalFeeWithCoupon = ({
    manualAdjustment = 0,
    quoteAmount,
    coupon,
}: UseCalculateTotalWithCouponsProps): {
    discount: number
    total: number
} => {
    let discount = 0
    let total = quoteAmount + manualAdjustment

    if (!coupon) {
        return {
            discount: 0,
            total: Number(total.toFixed(2)),
        }
    }

    const applyPercentageDiscount = (amount: number) => {
        discount = (amount * coupon.value) / 100
        return amount - discount
    }

    const applyFixedAmountDiscount = (amount: number) => {
        discount = coupon.value
        return amount - discount
    }

    if (coupon.type === 'percentage') {
        total = applyPercentageDiscount(total)
    } else if (coupon.type === 'fixed_amount') {
        if (coupon.limit_to !== 'time_slot_fee') {
            total = applyFixedAmountDiscount(total)
        }
    }

    return {
        discount: Number((amountToStripeSubUnit(discount) / 100).toFixed(2)),
        total: Number(total.toFixed(2)),
    }
}

export const getTotalFee = (
    disposalFee: number | undefined,
    weekendFee: number | undefined,
    timeSlotFee: number | undefined,
): number => {
    const total = (disposalFee || 0) + (weekendFee || 0) + (timeSlotFee || 0)
    // To 2 decimal places
    return Math.round(total * 100) / 100
}

export const pay = async (
    documentData: BookOrder,
    paymentMethodId: string,
    amount?: number,
) => {
    const url = isStaging
        ? 'https://europe-west2-staging-9c37f.cloudfunctions.net/chargeV2'
        : isQA
          ? 'https://europe-west2-book-litta-co.cloudfunctions.net/chargeV2_qa'
          : 'https://europe-west2-book-litta-co.cloudfunctions.net/chargeV2'
    const payload = {
        id: documentData.recordId,
        moto: true,
        paymentMethodId: paymentMethodId,
        ...(amount && { amount }),
    }

    const { data } = await axios.post(url, { data: payload })

    return data
}

export const createHolidays = (
    days: number,
    outerPostcode: string,
): Holiday[] => {
    const holidays: Holiday[] = []
    const today = new Date()

    for (let i = 0; i < days; i++) {
        const date = new Date(today)
        date.setDate(today.getDate() + i)
        holidays.push({
            Today: today,
            date: { seconds: Math.floor(date.getTime() / 1000) },
            date_string: date.toISOString(),
            day: date.getDate(),
            fee_override: null,
            month: date.getMonth() + 1,
            notes: null,
            status: 'closed',
            title: `Holiday on ${date.toDateString()}`,
            year: date.getFullYear(),
            selected_areas: [],
            closed_postcodes: [outerPostcode],
        })
    }

    return holidays
}

export const createSingleHoliday = (
    date: Date,
    outerPostcode: string,
): Holiday => {
    const today = new Date()

    const holiday: Holiday = {
        Today: today,
        date: { seconds: Math.floor(date.getTime() / 1000) },
        date_string: date.toISOString(),
        day: date.getDate(),
        fee_override: null,
        month: date.getMonth() + 1,
        notes: null,
        status: 'closed',
        title: `Holiday on ${date.toDateString()}`,
        year: date.getFullYear(),
        selected_areas: [],
        closed_postcodes: [outerPostcode],
    }

    return holiday
}

export const createDateObjectFromString = (
    dateString: string,
    timeslot?: string,
) => {
    const date = new Date()
    const [day, month, year] = dateString.split('-').map(v => Number(v))

    date.setFullYear(year, month - 1, day)

    if (timeslot) {
        const [startTime] = timeslot.split(':')
        date.setHours(Number(startTime))
    } else {
        date.setHours(7, 0, 0, 0)
    }

    return date
}

export const isLate = (
    order: BookOrder,
    minLimit = 120,
    jobStatusLookup: JobStatusLookup | null = null,
) => {
    if (isJobTerminalStatus(order, jobStatusLookup)) {
        return false
    }

    if (!order.selectedSlot) {
        return false
    }

    const timeRemaining = checkTimeSlotWindow(order.selectedSlot)
    const finalVal = timeRemaining <= minLimit

    return finalVal
}

export const requestNewCallList = async () => {
    const endpoint =
        'https://europe-west2-litta-technology.cloudfunctions.net/sales-request'
    await axios.get(endpoint)
}

export const getPaymentMethods = async (customerId: string) => {
    const endpoint = `https://api.litta.tech/v1/stripe/paymentMethods/${customerId}${isStaging ? '?isStaging=true' : ''}`

    const { data } = await axios.get(endpoint)
    return data as PaymentMethods
}
